import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事に必要な素養（route=/standards/aptitude）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /standards/infosec や /butler/become と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 設立1890年（明治23年）/ 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
// 創作した年号・数値・実績・合格率は書かない / 学歴・年齢による制限は設けていない

export default function StandardsAptitudePage() {
  useEffect(() => {
    updateSEO(seoData['/standards/aptitude']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事に必要な素養' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const reasonPillars = [
    {
      en: 'Shared Axis',
      ja: '共有できる座標',
      text: '素養は才能の有無を測るものではなく、現場で迷ったときに立ち返る座標です。誰が担当しても同じ方向へ判断を揃え、依頼者の生活の静けさを守るための共通言語になります。'
    },
    {
      en: 'Daily Practice',
      ja: '日々の手つきへ落とす',
      text: '理念だけを掲げても、朝の支度・取次・記録・言葉選びに落ちなければ続きません。基準として示すことで、短い確認と振り返りの対象にできます。'
    },
    {
      en: 'Open Entry',
      ja: '入口を閉ざさない',
      text: '当協会は学歴や年齢による制限を設けていません。素養は生まれ持った肩書ではなく、観察・記録・自制・学びの積み重ねとして開かれています。'
    }
  ];

  const observationLenses = [
    {
      title: '空気の変化',
      text: '声のトーン、間の取り方、席の立ち方など、言葉になる前の変化に気づきます。指摘ではなく、先回りの手配と静かな確認へつなげることが観察の目的です。'
    },
    {
      title: '物の配置',
      text: '常用の品の位置、季節の衣類、来客前の動線など、邸内の小さな乱れは生活のリズムの手がかりです。直す前に、直してよい範囲かを見極めます。'
    },
    {
      title: '人の関係',
      text: '誰と誰が同席し、どの話題が避けられているか。観察は詮索ではなく、場を整えるための情報です。得た手がかりを雑談の種にしない節度が伴います。'
    }
  ];

  const memoryHabits = [
    {
      en: 'Capture',
      ja: '捉える',
      text: '好み・禁忌・呼称・連絡の優先順位は、一度聞いただけでは散逸します。その場で短く残す習慣が、後の正確な再現を支えます。'
    },
    {
      en: 'Structure',
      ja: '整える',
      text: 'メモは量より見出しです。日付・場面・確定事項と未確定事項を分け、あとから他人が読んでも誤解しにくい形へ整えます。'
    },
    {
      en: 'Review',
      ja: '見返す',
      text: '記録は書いた瞬間に終わりません。行事の前後、担当の交代前、季節の変わり目に見返し、古くなった情報を閉じます。'
    },
    {
      en: 'Protect',
      ja: '守る',
      text: '残す場所と残さない場所を平時に決めます。記憶を補うための記録が、不用意な共有経路にならないよう扱います。'
    }
  ];

  const composureAxes = [
    {
      no: '01',
      title: '表情と呼吸',
      text: '予期せぬ変更や厳しい言葉のあとでも、表情と呼吸を先に整えます。動揺を消すのではなく、次の行動に渡さないことが自制です。'
    },
    {
      no: '02',
      title: '速度の抑制',
      text: '急ぐ場面ほど、確認の一手を省かない。速さは慌ただしさではなく、無駄な往復を減らす判断から生まれます。'
    },
    {
      no: '03',
      title: '私情の分離',
      text: '評価されたい気持ち、正しさを証明したい気持ちは、奉仕の邪魔になります。役割に戻る言葉を、心の中で一度唱えます。'
    },
    {
      no: '04',
      title: '報告のタイミング',
      text: '不安を抱えたまま抱え込まず、事実を短い文で共有します。感情の吐露ではなく、次に必要な判断材料を渡すことが目的です。'
    }
  ];

  const languageGuides = [
    {
      label: '敬語の核',
      lines: [
        '相手と場に応じた丁寧さを選び、過剰な謙譲で意味を濁さない',
        '身内と外部の呼び分けを誤り、関係者を不快にさせない',
        '誤りに気づいたら、長く弁明せず静かに言い直す'
      ]
    },
    {
      label: '伝える粒度',
      lines: [
        '結論と根拠を分け、推測を断定の形で置かない',
        '固有の事情を、必要のない第三者へ詳細に話さない',
        '短い文でも、敬意が残る語順と間を保つ'
      ]
    },
    {
      label: '沈黙の使い方',
      lines: [
        'すべてを埋めず、相手が考える余白を残す',
        '場の緊張を冗談や私見で和らげようとしない',
        '言わない選択自体が、信頼を守る仕事になる'
      ]
    }
  ];

  const prioritySteps = [
    {
      no: '01',
      title: '安全と尊厳',
      text: '人命、健康、プライバシーに関わる事柄を最優先に置きます。便利さや見栄えより、損なわれてはならないものを先に守ります。'
    },
    {
      no: '02',
      title: '約束の時刻',
      text: 'すでに交わした時間の約束は、他の改善案より重いことがあります。変更が必要なら、早めに選択肢を添えて相談します。'
    },
    {
      no: '03',
      title: '不可逆な手配',
      text: '取り消せない予約、外部への連絡、高額な発注は、確認の段を一段増やします。急ぐほど、一手の重さを数えます。'
    },
    {
      no: '04',
      title: '整えと余白',
      text: '優先度の低い美観や最適化は、主務のあとに回します。余力が生まれたときこそ、静かに場を整える順番です。'
    }
  ];

  const learningDomains = [
    {
      title: '教養',
      text: '歴史、芸術、食、季節の行事など、会話と手配の背景になる知識を細く長く積みます。自慢のための知識ではなく、相手の世界へ寄り添うための語彙です。'
    },
    {
      title: '語学',
      text: '来客や同行先に応じた基本的なやりとりを備え、必要に応じて専門の支援につなぎます。完璧な流暢さより、誤解を生まない確認の型が大切です。'
    },
    {
      title: '作法',
      text: '立ち居振る舞い、席次、贈り物の心得など、形は心を伝えるための器です。形だけを先行させず、その場の目的に合った作法を選びます。'
    }
  ];

  const qualMapping = [
    {
      tier: 'Associate',
      ja: '初級',
      text: '観察の基本、記録の残し方、敬語と自制の土台を身につけ、指示の意図を正確に実行できる状態を目指します。素養の「型」を身体に入れる段階です。'
    },
    {
      tier: 'Professional',
      ja: '中級',
      text: '複数の依頼が重なるなかで優先順位を判断し、先回りと報告の粒度を自分で調整します。素養が現場の再現性として現れる段階です。'
    },
    {
      tier: 'Master',
      ja: '上級',
      text: '家全体の空気と後進の手つきを見渡し、基準を言葉と示範で渡せます。専門資格とあわせ、領域ごとの深まりを設計する段階です。'
    }
  ];

  const faqItems = [
    {
      q: '素養は、生まれ持った気質がないと身につかないものですか？',
      a: 'いいえ。当協会が示す素養は、観察・記録・自制・言葉・段取り・学びといった、後から鍛えられる習慣の束です。気質の差は出発点の違いにすぎず、基準に沿った反復と振り返りで誰もが整えられます。学歴や年齢による制限も設けていません。'
    },
    {
      q: '資格の三段階と、素養の基準はどのように対応しますか？',
      a: 'Associateでは型の習得、Professionalでは判断の再現、Masterでは示範と設計へと、同じ素養を深さの違いとして育てます。専門資格は、特定領域でその深まりを補う位置づけです。詳細な認定の枠組みは認定基準と資格制度のページで確認できます。'
    },
    {
      q: '実務未経験からでも、素養の学びを始められますか？',
      a: '始められます。まずは言われる前に気づく観察と、短く残す記録、言葉を選ぶ習慣から着手できます。現場に入る前後を問わず、なり方の案内と資格の段階を参照しながら、自分の現在地に合った学びの順序を組んでください。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事に必要な素養｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事に必要な素養。基準として示す理由、観察力、記憶と記録の習慣、平常心と自制、言葉と敬語、段取りと優先順位、学び続ける姿勢、資格三段階との対応。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/aptitude'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事に必要な素養', item: 'https://japanbutlerassociation.com/standards/aptitude' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-aptitude-01.jpg"
          alt="静かな邸内で先回りと品位を備える執事の姿"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Aptitude Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事に必要な素養</span>
              <span className="block text-[#d7b779]">奉仕の品位を支える基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創設以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本頁は認定団体である当協会が定める「執事に必要な素養」の基準として、観察力、記憶と記録、平常心と自制、言葉と敬語、段取り、学び続ける姿勢、そして資格三段階との対応を示します。素養は肩書ではなく、毎日の短い手つきに宿る職能です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 理由 — 左紺縦帯＋三本ピラー（中央寄せ型とは別） */}
          <section>
            <div className="grid lg:grid-cols-[72px_1fr] gap-0 border border-[#e2ddd2] mb-10">
              <div className="bg-[#1a2942] flex lg:flex-col items-center justify-center gap-3 px-4 py-5 lg:py-10">
                <span className="font-['Playfair_Display',serif] text-[#d7b779] text-sm tracking-[0.2em] lg:writing-vertical-rl">
                  I
                </span>
                <span className="hidden lg:block w-px h-10 bg-[#a38e65]/50"></span>
                <span className="font-['Playfair_Display',serif] text-[#e7e2d8]/80 text-[10px] tracking-[0.18em] uppercase lg:[writing-mode:vertical-rl]">
                  Why Aptitude
                </span>
              </div>
              <div className="p-7 sm:p-10">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                  I. Why Define Aptitude
                </span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  素養を<span className="text-[#a38e65]">基準として示す理由</span>
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
                  執事の仕事は、見えない配慮の連続です。何を美徳とするかが人ごとに分かれると、依頼者は担当が変わるたびに空気の差を感じます。素養を基準として示すのは、個性を消すためではなく、品位の下限と向きを共有するためです。善意の熱量だけに頼らず、誰もが同じ問い——今、何を守り、何を後回しにするか——へ戻れるようにします。
                </p>
                <div className="grid md:grid-cols-3 gap-6">
                  {reasonPillars.map((item) => (
                    <div key={item.en} className="border-t-2 border-[#1a2942] pt-5">
                      <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{item.en}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.ja}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-center">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  基準の見取り図は
                  <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                  から俯瞰でき、認定の枠組みは
                  <a href="/standards/certification" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">認定基準</a>
                  と併せて読むと、素養がどこで評価の言葉に変わるかが分かります。素養は試験科目の暗記ではなく、邸内の短い判断の積み重ねです。
                </p>
                <p>
                  当協会はAssociate・Professional・Masterの三段階と専門資格を通じて、同じ素養を深さの違いとして育てます。入口を学歴や年齢で閉ざさず、いまの地点から型を身につけられる道を開いています。
                </p>
              </div>
              <img
                src="/images/generated/jba-std-aptitude-02.jpg"
                alt="素養の座標を共有するチームの打ち合わせ"
                className="w-full h-52 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S2. 観察力 — 上画像フル幅＋三本横バー */}
          <section>
            <div className="mb-8">
              <img
                src="/images/generated/jba-std-aptitude-03.jpg"
                alt="言われる前の変化に気づくため場を見渡す執事"
                className="w-full h-56 sm:h-64 object-cover border border-[#a38e65]/30"
              />
            </div>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
              <div>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                  II. Observation
                </span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em]">
                  観察力——言われる前に気づく
                </h2>
              </div>
              <p className="font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.14em] shrink-0">
                Notice · Discern · Act quietly
              </p>
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              優れた観察は、監視ではありません。相手が言葉にする前の不便や緊張を察知し、目立たない手配へ変える力です。気づいたすべてを動かすのではなく、動かす価値があるかを見極める判断が、観察の後半にあります。
            </p>
            <div className="space-y-0 border-t border-[#1a2942]">
              {observationLenses.map((item, idx) => (
                <div
                  key={item.title}
                  className={`grid sm:grid-cols-[200px_1fr] gap-4 sm:gap-8 py-6 ${
                    idx < observationLenses.length - 1 ? 'border-b border-[#e2ddd2]' : ''
                  }`}
                >
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mt-8">
              観察の成果は、大きな演出より、グラスの位置・室温・次の予定までの余白といった小さな整えに現れます。先回りが過剰になれば干渉になります。相手の自律を侵さない距離感を保ちながら、必要な一手だけを静かに置きます。
            </p>
          </section>

          {/* S3. 記憶と記録 — 2×2カード＋下リード（細罫縦スタックとは別） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. Memory & Record
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              記憶と記録のhabits
            </h2>
            <div className="bg-[#fbfaf7] border-l-2 border-[#a38e65] px-6 sm:px-8 py-6 mb-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#333] leading-[1.95]">
                記憶力への過信は、現場の穴になります。好みの変化、禁忌、呼称、連絡の順序は、頭の中だけに置かず、守れる形の記録へ移します。habit——習慣——として回すことで、多忙な日ほど再現性が残ります。
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              {memoryHabits.map((item) => (
                <div
                  key={item.en}
                  className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7"
                >
                  <div className="flex items-baseline gap-3 mb-3">
                    <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em]">{item.en}</span>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg">{item.ja}</h3>
                  </div>
                  <div className="w-8 h-px bg-[#a38e65] mb-4"></div>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              記録は多ければよいわけではありません。残す目的、閲覧できる範囲、破棄の時期を一文で言える状態を目指します。記憶を補う道具が、信頼を削る経路にならないよう、扱う場所と時間を限ることがhabitsの核心です。
            </p>
          </section>

          {/* S4. 平常心と自制 — 紺ヘッダ＋四分割グリッド */}
          <section>
            <div className="bg-[#1a2942] px-6 sm:px-10 py-8 sm:py-10 mb-0">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#d7b779] mb-3">
                IV. Composure
              </span>
              <h2 className="jba-display font-medium text-white text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-4">
                平常心と自制
              </h2>
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] max-w-3xl">
                平常心は感情の欠如ではなく、感情を役割の外へ漏らさない技術です。予定の変更、厳しい指摘、予期せぬ来客——揺さぶられる瞬間こそ、自制が奉仕の品質を決めます。
              </p>
            </div>
            <div className="grid sm:grid-cols-2 border border-t-0 border-[#e2ddd2] bg-white mb-8">
              {composureAxes.map((item) => (
                <div
                  key={item.no}
                  className="px-6 sm:px-8 py-6 border-b sm:border-r border-[#e2ddd2] odd:sm:border-r even:sm:border-r-0"
                >
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl block mb-2">{item.no}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[300px_1fr] gap-8 items-center">
              <img
                src="/images/generated/jba-std-aptitude-04.jpg"
                alt="平常心と自制を基礎研修で身につける学びの場"
                className="w-full h-48 object-cover border border-[#a38e65]/30"
              />
              <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                自制は一人で完結しません。事実を短い言葉で共有し、判断を委ねるべき事柄を抱え込まないことも、平常心を保つ手順です。個人の我慢比べにせず、型としての初動を持つことで、長い奉仕でも品位がすり減りにくくなります。
              </p>
            </div>
          </section>

          {/* S5. 言葉と敬語 — 三列ガイド＋下部一文（infosec連絡手段に近いが中身と配置を変える） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Language & Keigo
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              言葉の選択と敬語
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              執事の言葉は、情報の運搬手段であると同時に、場の温度を決める道具です。正しい敬語形を知っているだけでは足りず、誰に・どこで・どの長さで話すかという選択が素養になります。美しさより明瞭さ、多弁より適切な沈黙を尊びます。
            </p>
            <div className="grid lg:grid-cols-3 gap-6 mb-10">
              {languageGuides.map((block) => (
                <div key={block.label} className="border border-[#e2ddd2] bg-white p-7 flex flex-col">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-4 pb-4 border-b border-[#e2ddd2]">{block.label}</h3>
                  <ul className="space-y-3 mt-auto">
                    {block.lines.map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0 mt-0.5">▸</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm shrink-0">
                CLEAR · RESPECTFUL · BRIEF
              </span>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#e7e2d8] leading-[1.9]">
                言葉を飾ることと、相手を立てることは同じではありません。要件が届き、関係が傷つかない——その両立が、執事の語りの基準です。
              </p>
            </div>
          </section>

          {/* S6. 段取りと優先順位 — 縦番号プロセス＋右画像 */}
          <section>
            <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
              <div>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                  VI. Priority & Sequencing
                </span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                  段取りと優先順位の判断
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
                  同時に複数の依頼が重なるとき、すべてを均等に急ぐことはできません。何を先に守り、何を後に回し、何を相談して止めるか——段取りは優しさの順序付けです。優先順位を口に出せることは、現場を静かに保つ技術でもあります。
                </p>
                <div className="space-y-0">
                  {prioritySteps.map((item, idx) => (
                    <div
                      key={item.no}
                      className={`flex gap-5 sm:gap-6 py-5 ${
                        idx < prioritySteps.length - 1 ? 'border-b border-[#e2ddd2]' : ''
                      }`}
                    >
                      <span className="font-['Playfair_Display',serif] text-[#a38e65] text-2xl shrink-0 w-10">{item.no}</span>
                      <div>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-5 lg:sticky lg:top-28">
                <img
                  src="/images/generated/jba-std-aptitude-05.jpg"
                  alt="複数の依頼のなかで優先順位を判断する実務中の執事"
                  className="w-full h-56 object-cover border border-[#a38e65]/30"
                />
                <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">
                  段取りの上手さは、忙しそうに見せることではありません。次に何が起きるかを短く予告し、関係者が安心できる余白を残すことです。
                </p>
              </div>
            </div>
          </section>

          {/* S7. 学び＋資格対応＋FAQ — 統合セクション（7本目h2） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Learning & Qualification
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              学び続ける姿勢と、素養と資格三段階の対応
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              素養は一度身につけて終わりません。暮らしの様式、通信の手段、来客の背景は変わり続けます。教養・語学・作法を細く更新し、資格の段階に応じて深めることが、長い奉仕の条件です。なり方の道筋は
              <a href="/butler/become" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">執事になるには</a>
              でも案内しています。
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {learningDomains.map((item) => (
                <div key={item.title} className="bg-[#fbfaf7] border border-[#e2ddd2] p-7">
                  <div className="w-8 h-px bg-[#a38e65] mb-5"></div>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[14px] text-[#333] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-0 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">ASSOCIATE → PROFESSIONAL → MASTER</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                同じ素養を、深さの違いとして育てる
              </span>
            </div>
            <div className="grid lg:grid-cols-3 gap-0 border border-t-0 border-[#e2ddd2] bg-white mb-10">
              {qualMapping.map((item) => (
                <div key={item.tier} className="px-6 sm:px-7 py-7 border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#e2ddd2]">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-1">{item.tier}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{item.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-[1fr_280px] gap-8 items-start mb-12">
              <div className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] space-y-[18px]">
                <p>
                  専門資格は、三段階の縦の成長を横断する深まりとして位置づけます。領域が変わっても、観察・記録・自制・言葉・段取りという素養の芯は共通です。芯がぶれなければ、新しい知識は現場のノイズではなく、奉仕の精度へ変わります。
                </p>
                <p>
                  学びの進度は人それぞれです。当協会は学歴や年齢で入口を分けず、いまの実務と生活の条件に合わせて段階を踏めるよう設計しています。制度の全体像は
                  <a href="/qualifications" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">資格制度</a>
                  を参照してください。
                </p>
              </div>
              <img
                src="/images/generated/jba-std-aptitude-06.jpg"
                alt="後進へ素養を示範する上級執事のリーダーシップ"
                className="w-full h-56 object-cover border border-[#a38e65]/30"
              />
            </div>

            <div className="border-t-2 border-[#1a2942]">
              <p className="font-['Playfair_Display',serif] uppercase tracking-[0.18em] text-[12px] text-[#8f7a56] pt-6 mb-4">
                Questions
              </p>
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S8. 関連導線 — 9th section, 8th h2 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. Related Paths
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h2>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                素養の基準は、単独で完結するものではありません。認定の枠組みで評価の言葉を揃え、資格制度で段階を踏み、なり方の案内で学びの順序を確かめることで、日常の品位が続きます。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の座標を示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/certification" className={BTN08_ON_NAVY}>
                  認定基準
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
                <a href="/butler/become" className={BTN08_ON_NAVY}>
                  執事になるには
                </a>
              </div>
              <div className="mt-6">
                <a href="/standards" className={BTN08_ON_LIGHT}>
                  基準・指針の一覧
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
